import { Raycaster, Vector2, Vector3, type Camera, type Object3D } from "three";
import type { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { Engine, Entity } from "../../../engine/Engine";
import { History } from "../../../history/History";
import { SugarModel } from "../../../loader/SugarModel";
import { isSelectToolState } from "../../../core/scene/DesignerToolMode";
import type { DesignerElementEventChannels } from "../../../core/events/ElementEventPlatform";
import {
  DESIGNER_DRAG_ENDED_EVENT,
  DESIGNER_DRAG_STARTED_EVENT,
  DESIGNER_DRAWER_STATE_EVENT,
  DESIGNER_PRODUCT_TRANSFORMED_EVENT,
  DESIGNER_RENDER_MODE_CHANGED_EVENT,
  DESIGNER_REQUEST_RENDER_COMMAND,
  DESIGNER_SELECTION_CHANGED_EVENT,
  type DesignerSelectionChangedDetail,
} from "../../../core/events/DesignerEventContracts";
import type { RoomAnchorAdapter } from "../anchor/RoomAnchorAdapter";

type TranslateControls = TransformControls & {
  _gizmo?: { picker?: { translate?: Object3D } };
  showX: boolean;
  showY: boolean;
  showZ: boolean;
  showXY: boolean;
  showYZ: boolean;
  showXZ: boolean;
};

export type GizmoCaptureListener = (active: boolean) => void;

/** Float menüden açılan eksen modu. Kapalıyken oklar yok, gövde sürüklemesi durur. */
export class ProductGizmoMode {
  enabled = false;
  /** Oklar sahnedeyken ürün gövdesi collision sürüklemesi başlamaz. */
  blocksProductDrag = false;
}

/**
 * Ok / düzlem tutamacı kazanırken true. Press anındaki raycast
 * `dragging-changed`'ten önce de gövde sürüklemesini keser.
 */
export class ProductGizmoCapture {
  private _active = false;
  private _controls: TranslateControls | null = null;
  private _cameraSource: (() => Camera | undefined) | null = null;
  private readonly _listeners = new Set<GizmoCaptureListener>();
  private readonly _raycaster = new Raycaster();
  private readonly _ndc = new Vector2();

  get active(): boolean {
    return this._active;
  }

  bind(controls: TransformControls | null): void {
    this._controls = controls;
  }

  /** 2D/3D geçişinde `camera.instance` değişir; raycast güncel kamerayı kullanır. */
  setCameraSource(source: (() => Camera | undefined) | null): void {
    this._cameraSource = source;
  }

  /** Tutamaç üzerinde mi? Listener sırasından bağımsız. */
  hitsPointer(clientX: number, clientY: number): boolean {
    const controls = this._controls;
    if (!controls?.enabled || controls.object === undefined) return false;
    const liveCamera = this._cameraSource?.();
    if (liveCamera && controls.camera !== liveCamera) controls.camera = liveCamera;
    if (controls.dragging || controls.axis) return true;
    const picker = controls._gizmo?.picker?.translate;
    const dom = controls.domElement;
    const camera = controls.camera;
    if (!picker || !dom || !camera) return false;
    const rect = dom.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    this._ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this._raycaster.setFromCamera(this._ndc, camera);
    const hits = this._raycaster.intersectObject(picker, true);
    return hits.some((hit) => hit.object.visible);
  }

  setActive(active: boolean): void {
    if (this._active === active) return;
    this._active = active;
    for (const listener of this._listeners) listener(active);
  }

  subscribe(listener: GizmoCaptureListener): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }
}

type GizmoContext = {
  tool: string;
  /** Üstten plan kamerası. Y ekseni bakış yönüne paralel olduğu için gizlenir. */
  plan: boolean;
  product?: SugarModel;
};

/**
 * Seçili ürüne dünya uzayında translate oku bağlar.
 * Tutamaç sürüklemesi constraint pipeline'ına girmez.
 */
export class ProductTranslateGizmo extends Entity {
  private _controls?: TranslateControls;
  private _boundEngine?: Engine;
  private _dragModel?: SugarModel;
  private readonly _dragOrigin = new Vector3();
  private readonly _detach: Array<() => void> = [];

  constructor(
    private readonly _channels: DesignerElementEventChannels,
    private readonly _capture: ProductGizmoCapture,
    private readonly _mode: ProductGizmoMode,
    private readonly _anchors: RoomAnchorAdapter | undefined,
    private readonly _context: () => GizmoContext,
  ) {
    super();
  }

  override onAdd(engine: Engine): void {
    super.onAdd(engine);
    this._boundEngine = engine;
    const controls = engine.transformControls as TranslateControls;
    controls.setMode("translate");
    controls.setSpace("world");
    controls.enabled = false;
    controls.detach();
    controls.getHelper().traverse((node) => {
      node.userData.__editorHidden = true;
      node.frustumCulled = false;
    });
    this._controls = controls;
    this._capture.bind(controls);
    this._capture.setCameraSource(() => this._host()?.activeCamera?.instance);

    const onDraggingChanged = (event: { value?: unknown }): void => {
      if (event.value) this._beginCapture();
      else this._endCapture();
    };
    const onObjectChange = (): void => {
      this._emitTransformed();
    };
    controls.addEventListener(
      "dragging-changed",
      onDraggingChanged as never,
    );
    controls.addEventListener("objectChange", onObjectChange as never);
    this._detach.push(() => {
      controls.removeEventListener(
        "dragging-changed",
        onDraggingChanged as never,
      );
      controls.removeEventListener("objectChange", onObjectChange as never);
    });

    this._detach.push(
      this._channels.selection.events.on(
        DESIGNER_SELECTION_CHANGED_EVENT,
        (event: CustomEvent<DesignerSelectionChangedDetail>) => {
          void event;
          this._sync();
        },
      ),
      this._channels.configuration.events.on(
        DESIGNER_DRAWER_STATE_EVENT,
        () => this._sync(),
      ),
      this._channels.configuration.events.on(
        DESIGNER_RENDER_MODE_CHANGED_EVENT,
        () => this._sync(),
      ),
    );

    engine.canvas.addEventListener("pointercancel", this._onPointerCancel);
    engine.addEventListener("product-gizmo-mode", this._onMode as EventListener);
    this._detach.push(() => {
      engine.canvas.removeEventListener(
        "pointercancel",
        this._onPointerCancel,
      );
      engine.removeEventListener(
        "product-gizmo-mode",
        this._onMode as EventListener,
      );
    });

    this._sync();
  }

  override onRemove(engine: Engine): void {
    this._boundEngine = engine;
    this._releaseControls();
    this._boundEngine = undefined;
    for (const detach of this._detach.splice(0)) detach();
    this._capture.bind(null);
    this._capture.setCameraSource(null);
    this._controls = undefined;
    super.onRemove(engine);
  }

  private readonly _onMode = (event: Event): void => {
    this._mode.enabled =
      (event as CustomEvent<{ enabled?: boolean }>).detail?.enabled === true;
    this._sync();
  };

  private readonly _onPointerCancel = (): void => {
    const controls = this._controls;
    if (!controls?.dragging) return;
    controls.dragging = false;
  };

  override beforeRender(): void {
    this._bindActiveCamera();
  }

  private _bindActiveCamera(): void {
    const controls = this._controls;
    if (!controls) return;
    const camera = this._host()?.activeCamera?.instance;
    if (!camera || controls.camera === camera) return;
    controls.camera = camera;
  }

  private _sync(): void {
    const controls = this._controls;
    if (!controls) return;
    const { tool, plan, product } = this._context();
    const next =
      isSelectToolState(tool) && this._mode.enabled ? product : undefined;
    if (!next) {
      this._releaseControls();
      return;
    }
    this._bindActiveCamera();
    controls.setMode("translate");
    controls.setSpace("world");
    controls.showX = true;
    controls.showZ = true;
    controls.showXZ = true;
    controls.showY = !plan;
    controls.showXY = !plan;
    controls.showYZ = !plan;
    controls.enabled = true;
    this._placeHelper(true);
    if (controls.object !== next) {
      if (controls.dragging) controls.dragging = false;
      controls.attach(next);
      this._requestRender();
    }
    this._mode.blocksProductDrag = true;
  }

  /** Swap render sahnesi gizli root'un çocuklarını çekmesin diye helper'ı sahneden çıkar. */
  private _placeHelper(attached: boolean): void {
    const controls = this._controls;
    const scene = this._host()?.activeScene;
    if (!controls || !scene) return;
    const helper = controls.getHelper();
    if (attached) {
      if (helper.parent !== scene) scene.add(helper);
      return;
    }
    helper.removeFromParent();
  }

  private _releaseControls(): void {
    const controls = this._controls;
    if (!controls) return;
    if (controls.dragging) controls.dragging = false;
    if (controls.object) controls.detach();
    controls.enabled = false;
    this._placeHelper(false);
    this._mode.blocksProductDrag = false;
    this._requestRender();
  }

  private _beginCapture(): void {
    if (this._capture.active) return;
    const model = this._controls?.object;
    if (!(model instanceof SugarModel)) return;
    this._dragModel = model;
    this._dragOrigin.copy(model.position);
    this._capture.setActive(true);
    this._channels.transform.events.emit(DESIGNER_DRAG_STARTED_EVENT, undefined);
    this._host()?.dispatchCustomEvent("transform-start");
  }

  private _endCapture(): void {
    if (!this._capture.active) return;
    const model = this._dragModel;
    const moved =
      !!model &&
      model.position.distanceToSquared(this._dragOrigin) > 1e-10;
    this._dragModel = undefined;
    this._capture.setActive(false);
    this._channels.transform.events.emit(DESIGNER_DRAG_ENDED_EVENT, undefined);
    this._host()?.dispatchCustomEvent("transform-end");
    if (!model || !moved) return;
    model.updateMatrixWorld(true);
    this._anchors?.setProductAnchors(model, [
      {
        kind: "floor",
        x: model.position.x,
        z: model.position.z,
        y: model.position.y,
      },
    ]);
    const host = this._host();
    if (host) History.of(host)?.snapshot("Move product");
    this._channels.transform.events.emit(DESIGNER_PRODUCT_TRANSFORMED_EVENT, {
      model,
    });
    this._requestRender();
  }

  private _host(): Engine | undefined {
    return this.engine ?? this._boundEngine;
  }

  private _requestRender(): void {
    this._channels.transform.commands.emit(
      DESIGNER_REQUEST_RENDER_COMMAND,
      undefined,
    );
  }

  private _emitTransformed(): void {
    const model = this._dragModel;
    if (!this._capture.active || !model) return;
    model.updateMatrixWorld(true);
    this._channels.transform.events.emit(DESIGNER_PRODUCT_TRANSFORMED_EVENT, {
      model,
    });
    this._requestRender();
  }
}
